import { useCallback, useEffect, useState } from "react";

import {
  microphoneAccessFrom,
  type MicrophoneAccess,
} from "@/engine/media/microphoneAccess";

declare global {
  /**
   * Neither `microphone` nor `camera` is in TypeScript's `PermissionName`
   * union, though every browser that answers for them accepts both names.
   */
  interface Permissions {
    query(descriptor: {
      name: "microphone" | "camera";
    }): Promise<PermissionStatus>;
  }
}

/** The two devices a room asks the browser for. */
export type DeviceKind = "microphone" | "camera";

export interface DevicePermission {
  /** What the browser says, without anybody having been prompted. */
  access: MicrophoneAccess;
  /** A prompt is open right now. */
  asking: boolean;
  /**
   * Ask the browser for the device, as a press rather than a side effect.
   *
   * The Permissions API is silent on some browsers and outright missing on
   * others, so the answer here comes from the capture itself: a stream means
   * granted, a refusal means refused. The tracks are stopped straight away —
   * this only exists so the device names become readable and the visitor can
   * see where they stand.
   */
  ask(): Promise<MicrophoneAccess>;
}

/**
 * Where a visitor stands with one device, and the button that asks.
 *
 * The automatic prompt fails on enough exotic browsers that the manual one has
 * to exist beside the device list rather than only inside the room controls.
 */
export function useDevicePermission(
  kind: DeviceKind,
  onGranted?: () => void,
): DevicePermission {
  const [access, setAccess] = useState<MicrophoneAccess>("unknown");
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    const permissions = navigator.permissions;
    if (!permissions?.query) return;

    let status: PermissionStatus | null = null;
    let cancelled = false;
    const read = () => {
      if (!cancelled) setAccess(microphoneAccessFrom(status?.state));
    };

    void (async () => {
      try {
        // A browser without this permission name rejects rather than answers,
        // and that is not a refusal.
        status = await permissions.query({ name: kind });
      } catch {
        return;
      }
      if (cancelled) return;
      status.addEventListener("change", read);
      read();
    })();

    return () => {
      cancelled = true;
      status?.removeEventListener("change", read);
    };
  }, [kind]);

  const ask = useCallback(async () => {
    setAsking(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        kind === "microphone" ? { audio: true } : { video: true },
      );
      stream.getTracks().forEach((track) => track.stop());
      setAccess("granted");
      onGranted?.();
      return "granted" as const;
    } catch {
      setAccess("refused");
      return "refused" as const;
    } finally {
      setAsking(false);
    }
  }, [kind, onGranted]);

  return { access, asking, ask };
}
